/**
 * Wishlist heart on product cards (collection and search grids, Athos recommendations, theme cards).
 *
 * The hearts are `.wishlist-collection-button .wkh-button[data-product-handle]` buttons, rendered by
 * blocks/product-wishlist-btn.liquid and by the Athos card template. The Athos template toggles them
 * itself, but it keeps its own copy of the wishlist (a bare fetch with no Swish session) and nothing
 * subscribes to the Swish wishlist state. So Swish ignores every add / remove after the first while
 * that action is pending, no heart looks saved after a reload, and a saved heart can't be removed.
 *
 * Clicks on these buttons are handled here instead, through the Swish window API
 * (`window.WishlistKing`), the same state and actions its own PDP / collection buttons use: the
 * wishlist is read with `state.observeWishlist` (that subscription is also what clears Swish's
 * pending action) and items are saved / removed with `addWishlistItem / removeWishlistItem`. Cards
 * are matched by product handle and the SDK resolves the product id itself. Selected state =
 * `wk-selected` class, which assets/base.css already styles.
 */
(() => {
  // Guards against this script running twice on the same page (theme editor hot-reload, a section
  // refresh that re-runs scripts, ...). A second run would register a second click listener and the
  // two toggles of a single click would cancel each other out.
  if (window.__wishlistCardButtonInit) return;
  window.__wishlistCardButtonInit = true;

  // The header wishlist link is an `a.wkh-button` outside `.wishlist-collection-button`, so it is not matched.
  const BUTTON_SELECTOR = '.wishlist-collection-button .wkh-button[data-product-handle]';
  /** How long a click waits for the first wishlist state before acting on an empty one. */
  const STATE_TIMEOUT_MS = 4000;

  /** Wishlist item id by product handle, kept in sync with the Swish wishlist state. */
  let wishlistItemIds = new Map();
  let stateLoaded = false;
  /** @type {() => void} */
  let resolveState;
  const stateReady = new Promise((resolve) => {
    resolveState = resolve;
  });

  /** @type {Promise<any> | undefined} */
  let appPromise;

  /** Resolves with `window.WishlistKing` as soon as the Swish SDK has loaded. */
  function getApp() {
    appPromise ??= new Promise((resolve) => {
      if (window.WishlistKing) {
        resolve(window.WishlistKing);
      } else {
        document.addEventListener('wk:app:ready', () => resolve(window.WishlistKing), { once: true });
      }
    });
    return appPromise;
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} selected
   */
  function setSelected(button, selected) {
    button.classList.toggle('wk-selected', selected);
    button.setAttribute('aria-pressed', String(selected));
  }

  function syncSelectedStates() {
    if (!stateLoaded) return;
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const handle = String(button.dataset.productHandle || '').toLowerCase();
      setSelected(button, Boolean(handle) && wishlistItemIds.has(handle));
    });
  }

  /** @param {HTMLElement} button */
  async function toggle(button) {
    try {
      const handle = String(button.dataset.productHandle || '').toLowerCase();
      if (!handle) return;
      const app = await getApp();
      await Promise.race([stateReady, new Promise((resolve) => setTimeout(resolve, STATE_TIMEOUT_MS))]);
      const wishlistItemId = wishlistItemIds.get(handle);
      // The heart follows the Swish wishlist state (subscription below), so nothing is set here.
      if (wishlistItemId) {
        await app.removeWishlistItem({ wishlistItemId });
      } else {
        await app.addWishlistItem({ productHandle: handle });
      }
    } catch (error) {
      console.warn('[Wishlist card] toggle warning:', error);
    } finally {
      togglingButtons.delete(button);
    }
  }

  const togglingButtons = new WeakSet();

  // Capture phase: the Athos template puts its own click handler on the button itself, which would
  // otherwise run first (and wrongly add an item that is already saved). Until the Swish SDK exists
  // the click is left alone so the hearts keep working as before.
  document.addEventListener(
    'click',
    (event) => {
      if (!window.WishlistKing) return;
      const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
      if (!(button instanceof HTMLElement)) return;
      event.preventDefault();
      // stopImmediatePropagation (not just stopPropagation) so that if this listener ever ends up
      // registered more than once, a single click can't double-fire and cancel itself out.
      event.stopImmediatePropagation();
      event.stopPropagation();
      if (togglingButtons.has(button)) return;
      togglingButtons.add(button);
      toggle(button);
    },
    true
  );

  getApp()
    .then((app) => {
      app.state.observeWishlist({ wishlistId: 'mine' }).subscribe((wishlist) => {
        wishlistItemIds = new Map(wishlist.items.map((item) => [String(item.product.handle).toLowerCase(), item.id]));
        stateLoaded = true;
        resolveState();
        syncSelectedStates();
      });
    })
    .catch((error) => console.warn('[Wishlist card] could not read the Swish wishlist state:', error));

  // Athos renders its cards asynchronously and again on every sort / filter / page change, and the
  // recommendations load late, so keep the hearts in sync whenever the DOM changes.
  let timer;
  function queueSync() {
    if (timer) return;
    timer = setTimeout(() => {
      timer = undefined;
      syncSelectedStates();
    }, 50);
  }

  new MutationObserver(queueSync).observe(document.body, { childList: true, subtree: true });
  queueSync();
})();
